import { ColDef, ColumnMovedEvent, ColumnPinnedEvent, ColumnResizedEvent, ColumnState, FilterChangedEvent, GridApi, GridReadyEvent, IAggFunc, IAggFuncParams, ICellRendererParams, RowClassParams, RowSelectedEvent, ValueFormatterParams, ValueGetterParams } from '@ag-grid-community/core';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableInterface } from '../interfaces/table';
import { EnumColumnType, Field } from '../../../interfaces/field';
import moment from 'moment';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { BehaviorSubject, EMPTY, Observable, ReplaySubject, Subject, catchError, combineLatest, debounceTime, filter, finalize, interval, map, merge, of, switchMap } from 'rxjs';
import { DashboardViewService } from 'src/app/services/dashboard.services';
import { DashBoardInterface, DashBoardTypeEnum, DashBoardUserConfigInterface } from 'src/app/system-view/main-dashboard/interfaces/dashboard';
import { TableCardConfigurationComponent } from './table-card-configuration/table-card-configuration.component';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { DashboardFocusService } from 'src/app/services/dashboard-focus.service';
import { GadGetAction } from '../interfaces/gadget-events';
import { FormGroup } from '@angular/forms';
import { DateUtils } from 'src/app/system-utils/utils/DateUtils';
import { ThemeService } from 'src/app/services/theme.service';
import { LanguageServices } from 'src/app/services/LanguageServices';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { compileExpressionFormula, compileFormula, sanitizeCssClassList } from '../safe-formula';
import { openGadgetModal } from '../gadget-modal';

/*
 * Módulos NG-ZORRO usados no template: NzButtonModule, NzIconModule,
 * NzDividerModule, NzDropDownModule, NzFloatButtonModule, NzToolTipModule
 * (todos via SharedModule).
 *
 * Fluxo de dados: combineLatest(gridReady$, trigger$) + switchMap —
 * nunca toca o gridApi antes de existir, a requisição anterior é
 * cancelada, e o replay do filtro da aba cobre assinantes tardios.
 */
@Component({
    selector: 'table-card',
    templateUrl: './table-card.component.html',
    styleUrl: './table-card.component.scss',
    standalone: false
})
export class TableCardComponent implements OnInit {
  lastUpdate = "01/01/1900 23:59:59";
  isRefreshing = false;

  displayedColumns: ColDef[] = [];
  rows: unknown[] = [];
  @Input() showConfiguration: Boolean = false;
  focus = false;
  userConfig: DashBoardUserConfigInterface = null;
  @Input() name: string;
  tabName: string;
  formFilter: FormGroup;
  idDashBoard: number;
  @Input() dashboard: DashBoardInterface;
  @Input() events: Observable<FormGroup | null>;
  @Input() item: TableInterface;
  @Output() itemChange: EventEmitter<TableInterface> = new EventEmitter<TableInterface>();
  @Output() deleting: EventEmitter<GadGetAction> = new EventEmitter<GadGetAction>();
  selectedRows: { index: number, data: Object }[] = [];
  showPagination = true;
  canClipboard = false;
  canExport = false;

  private gridApi!: GridApi;
  private readonly gridReady$ = new ReplaySubject<GridApi>(1);
  private readonly manualReload$ = new Subject<void>();
  private readonly refreshSeconds$ = new BehaviorSubject<number | null>(null);
  private readonly filterChanged$ = new Subject<GridApi>();
  private readonly stateSave$ = new Subject<void>();

  private readonly currencyPipe = new CurrencyPipe('pt-BR');
  private readonly decimalPipe = new DecimalPipe('pt-BR');

  aggFuncs: Record<string, IAggFunc<any, any>> = {
    sum: (params: IAggFuncParams<any, any>) => {
      const values = params.values as any[];
      return values.reduce((a, b) => a + b, 0);
    },
    count: (params: IAggFuncParams<any, any>) => {
      const values = params.values as any[];
      return values.length;
    },
    avg: (params: IAggFuncParams<any, any>) => {
      const values = params.values as any[];
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    },
    min: (params: IAggFuncParams<any, any>) => {
      const nums = (params.values as any[]).map(Number).filter(v => !isNaN(v));
      return nums.length ? Math.min(...nums) : null;
    },
    max: (params: IAggFuncParams<any, any>) => {
      const nums = (params.values as any[]).map(Number).filter(v => !isNaN(v));
      return nums.length ? Math.max(...nums) : null;
    },
    first: (params: IAggFuncParams<any, any>) => {
      const values = params.values as any[];
      return values.length ? values[0] : null;
    },
    last: (params: IAggFuncParams<any, any>) => {
      const values = params.values as any[];
      return values.length ? values[values.length - 1] : null;
    }
  };

  public columnTypes: { [key: string]: ColDef } = {
    numberColumn: { width: 130, filter: "agNumberColumnFilter" },
    nonEditableColumn: { editable: false },
    currency: {
      valueFormatter: (data) => this.currencyPipe.transform(data.value, "BRL")
    },
    dateColumn: {
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
          const dateParts = cellValue.split("/");
          const day = Number(dateParts[0]);
          const month = Number(dateParts[1]) - 1;
          const year = Number(dateParts[2]);
          const cellDate = new Date(year, month, day);
          if (cellDate < filterLocalDateAtMidnight) {
            return -1;
          } else if (cellDate > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        },
      },
    },
  };

  constructor(private dvs: DashboardViewService,
    private message: NzMessageService,
    private modal: NzModalService,
    private viewContainerRef: ViewContainerRef,
    private uas: UserAuthenticationServices,
    public ThemeService: ThemeService,
    public ls: LanguageServices,
    public dfs: DashboardFocusService,
    private destroyRef: DestroyRef) {

  }

  ngOnInit(): void {

    this.tabName = this.dashboard?.tabName;
    this.idDashBoard = this.dashboard?.iddashboard;

    this.dfs.focusChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(c => this.focus = c);

    this.filterChanged$
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((api: GridApi) => this.calcularTotaisRodape(api, this.displayedColumns));

    this.stateSave$
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveUserConfig());

    this.refreshSeconds$.next(this.item?.refresh ?? null);

    // gatilhos de carga: filtro da aba (replay), recarga manual, tick de refresh
    const filterTrigger$ = (this.events ?? EMPTY).pipe(
      map(form => {
        if (form != null) {
          this.formFilter = form;
        }
        return void 0;
      })
    );

    const refreshTick$ = this.refreshSeconds$.pipe(
      switchMap(seconds => (seconds != null && seconds > 0) ? interval(seconds * 1000) : EMPTY),
      filter(() => this.focus && !this.isRefreshing),
      map(() => void 0)
    );

    const trigger$ = merge(filterTrigger$, this.manualReload$, refreshTick$);

    combineLatest([this.gridReady$, trigger$])
      .pipe(
        switchMap(([api]) => this.fetchRows$(api)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        if (result != null) {
          this.afterDataLoaded(result.api, result.rows);
        }
      });
  }

  /**
   * Busca as linhas da query com os filtros aplicados. O switchMap do
   * pipeline cancela a requisição anterior — sem respostas fora de ordem.
   */
  private fetchRows$(api: GridApi): Observable<{ api: GridApi, rows: any[] } | null> {
    if (this.item?.query == null) {
      // gadget sem query: renderiza vazio, sem erro (bug antigo: .then de undefined)
      this.itemChange.emit(this.item);
      return of(null);
    }

    const executeQuery = this.dvs.applyFilters(this.item.query, this.formFilter);
    this.isRefreshing = true;

    return this.dvs.loadData(executeQuery).pipe(
      map(data => ({ api, rows: (data['SQL'] ?? []) as any[] })),
      catchError(() => {
        this.message.error(`Falha ao carregar os dados da tabela "${this.item?.title ?? this.name}".`);
        this.itemChange.emit(this.item);
        return of(null);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  /** ordem única e garantida: linhas → colunas → estado → cores → totais */
  private afterDataLoaded(api: GridApi, rows: any[]): void {
    this.lastUpdate = DateUtils.getNowFormatted("DD/MM/YYYY HH:mm:ss.SSS");
    this.rows = rows;
    this.displayedColumns = this.buildColumns();
    // columnDefs via API (síncrono) — com binding no template o
    // applyColumnState rodava ANTES das colunas novas chegarem e o
    // grid resetava as larguras salvas pelo usuário
    api.setGridOption('columnDefs', this.displayedColumns);
    api.setGridOption('rowData', rows);
    this.applyColumnState(api);
    this.applyGridColors(api);
    this.calcularTotaisRodape(api, this.displayedColumns);
    this.itemChange.emit(this.item);
  }

  onGridReady(event: GridReadyEvent): void {

    this.gridApi = event.api;
    this.showPagination = this.item?.options?.showPagination ?? true;
    this.canClipboard = this.item?.options?.canClipboard ?? false;
    this.canExport = this.item?.options?.canExport ?? false;

    // preferência de colunas ANTES de liberar o grid para o pipeline —
    // garante applyColumnState com o estado carregado
    this.dvs.loadPreference(this.uas.user.iduser, this.resourceId())
      .then(pref => {
        if (pref != null) {
          this.userConfig = {
            columnState: JSON.parse(pref)['columnState'] as ColumnState[],
          };
        }
      })
      .catch(() => { /* preferência é opcional */ })
      .finally(() => this.gridReady$.next(event.api));
  }

  /** recarga manual (botão de refresh do template) */
  reload(): void {
    if (!this.isRefreshing) {
      this.manualReload$.next();
    }
  }

  onFilterChanged(event: FilterChangedEvent): void {
    this.filterChanged$.next(event.api);
  }

  calcularTotaisRodape(api: GridApi, columnDefs: ColDef[]): void {

    if (api == null || columnDefs.findIndex(d => d.aggFunc != null && d.aggFunc != 'none') == -1) {
      return;
    }

    const rows: any[] = [];
    api.forEachNodeAfterFilter((node) => {
      if (!node.group) rows.push(node.data);
    });

    const totals: any = {};
    columnDefs.forEach(col => {
      const field = col.field;
      const aggFunc = col.aggFunc;
      if (field && aggFunc && aggFunc !== 'none') {
        const values = rows.map(row => row[field]).filter(v => v != null);

        if (typeof aggFunc === 'function') {
          totals[field] = (aggFunc as IAggFunc<any, any>)({ values } as IAggFuncParams<any, any>);
        } else if (typeof aggFunc === 'string') {
          const fn = (this.aggFuncs as any)[aggFunc] as IAggFunc<any, any> | undefined;
          totals[field] = (fn && typeof fn === 'function') ? fn({ values } as IAggFuncParams<any, any>) : null;
        } else {
          totals[field] = null;
        }
      } else {
        totals[field] = null;
      }
    });

    api.setGridOption('pinnedBottomRowData', [totals]);
  }

  /** compila a fórmula de cor UMA vez e nunca deixa exceção derrubar o grid */
  private applyGridColors(api: GridApi): void {
    const formula = this.item?.options?.colorLinesFormula;
    if (formula == null || formula === '') {
      api.setGridOption('getRowStyle', null);
      return;
    }
    const fn = compileFormula<RowClassParams, any>(`if (params.data == null) { return; }\n${formula}`);
    api.setGridOption('getRowStyle', fn ?? null);
  }

  /**
   * Constrói as colunas de forma SÍNCRONA a partir da configuração.
   * Fórmulas (ícone) e cellStyle são compilados/parseados uma vez por
   * coluna — o código antigo dava eval/JSON.parse por célula.
   */
  private buildColumns(): ColDef[] {

    const fields = this.item?.options?.fields;
    if (fields == null) {
      return [];
    }

    const sorted = [...fields].sort((a: Field, b: Field) => {
      if (a.order > b.order) return 1;
      if (a.order < b.order) return -1;
      return 0;
    });

    const columns: ColDef[] = [];

    for (const dataColumn of sorted) {

      let parsedCellStyle: { [key: string]: string } | null = null;
      if (dataColumn.cellStyle != null) {
        try {
          parsedCellStyle = JSON.parse(dataColumn.cellStyle) as { [key: string]: string };
        } catch {
          parsedCellStyle = null;
        }
      }

      const column: ColDef = {
        field: dataColumn.columnName,
        width:
          dataColumn.width == null || dataColumn.width == 0
            ? 80
            : dataColumn.width,
        headerName:
          dataColumn.headerName == null ?
            dataColumn.columnDesc == null
              ? dataColumn.columnName
              : dataColumn.columnDesc : dataColumn.headerName,

        sortable: dataColumn.sortable,
        hide: dataColumn.hide,
        filter: dataColumn.filter,
        headerTooltip: dataColumn.headerTooltip == null ? dataColumn.columnDesc : dataColumn.headerTooltip,
        resizable: dataColumn.resizable,
        maxWidth: dataColumn.maxWidth == null ? 600 : dataColumn.maxWidth,
        pinned: dataColumn.pinned,
        colId: dataColumn.columnName,
        cellStyle: (params) => {
          if (parsedCellStyle != null) {
            return parsedCellStyle;
          }
          if (params.node?.rowPinned) {
            return { fontWeight: 'bold' };
          }
          return null;
        },
        sort: dataColumn.sort == null ? null : dataColumn.sort == 'asc' ? 'asc' : 'desc',
        aggFunc: dataColumn.aggFunc == null ? null : dataColumn.aggFunc
      };

      if (dataColumn.type == EnumColumnType.Date || dataColumn.type == EnumColumnType.DateTime) {

        column.valueGetter = this.createDateValue(dataColumn.columnName);
        column.valueFormatter = this.createDateFormatter(dataColumn);
        column.filter = dataColumn.filter ? 'agDateColumnFilter' : null;
        column.filterParams = {
          comparator: (filterDate: Date, cellValue: string) => {
            const cellDate = new Date(cellValue);
            if (cellDate < filterDate) return -1;
            if (cellDate > filterDate) return 1;
            return 0;
          }
        };
      }

      if (dataColumn.type == EnumColumnType.Number) {
        column.type = ['numericColumn'];
        column.valueParser = 'Number(newValue)';
        column.valueFormatter = this.createNumberFormatter(dataColumn);
      }

      if (dataColumn.type == EnumColumnType.Integer) {
        column.type = ["numericColumn", "rightAligned"];
        column.valueFormatter = (data: ValueFormatterParams) => {
          if (data.value === "null" || data.value === null || data.value === "") {
            return "";
          }
          const parsed = Number(data.value);
          return isNaN(parsed) ? '-1' : Math.floor(parsed).toString();
        };
      }

      if (dataColumn.type == EnumColumnType.Selection) {
        const resolveSelection = this.createSelectionResolver(dataColumn);
        column.cellRenderer = (params: ICellRendererParams) => {
          if (params.value === "null" || params.value === null || params.value === "") {
            return "";
          }
          return `<span>${resolveSelection(String(params.value))}</span>`;
        };
      }

      if (dataColumn.type == EnumColumnType.Icon) {
        // compilada UMA vez por coluna; saída sanitizada (whitelist de
        // classes CSS) antes de ir para innerHTML — fecha stored-XSS
        const iconFn = compileExpressionFormula<unknown, unknown>(dataColumn.formulaIcon);
        column.cellRenderer = (params: ICellRendererParams) => {
          if (params.value === "null" || params.value === null || params.value === "") {
            return "";
          }
          if (iconFn == null) {
            return `<span>${sanitizeCssClassList(String(params.value)) ?? ''}</span>`;
          }
          const cssClass = sanitizeCssClassList(iconFn(params.value));
          return cssClass != null
            ? `<i class="${cssClass}"></i><span class="span10"></span>`
            : '';
        };
      }

      columns.push(column);
    }

    return columns;
  }

  /** resolve o "valor - descrição" do tipo Selection (parse uma vez por coluna) */
  private createSelectionResolver(column: Field): (data: string) => string {
    const validationError = column.validationError;
    let options: object[] | null = null;
    try {
      const ob: object = JSON.parse(validationError);
      options = ob != null ? ob["options"] as object[] : null;
    } catch {
      options = null;
    }

    return (data: string) => {
      if (options != null) {
        const dataValue = options.find((val) => val["value"] == data);
        return dataValue != null ? `${data} - ${dataValue["view"]}` : data;
      }
      // formato legado "valor=descricao"
      if (validationError != null) {
        for (const value of (Array.from(validationError) as string[])) {
          if (value.split('=')[0] == data) {
            return value.split('=')[0] + " - " + value.split('=')[1];
          }
        }
      }
      return data;
    };
  }

  createDateValue(columnName: string): (params: ValueGetterParams) => Date | null {
    return (params: ValueGetterParams) => {
      const raw: string = params.data?.[columnName];
      if (!raw) return null;
      const d = new Date(raw);
      return new Date(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
        d.getUTCHours(),
        d.getUTCMinutes(),
        d.getUTCSeconds(),
        d.getUTCMilliseconds()
      );
    };
  }

  createDateFormatter(dataColumn: Field): (params: ValueFormatterParams) => string {
    return (params: ValueFormatterParams) => {
      const value = params.value;
      if (value === "null" || value === null || value === "") {
        return "";
      }

      try {
        if (!dataColumn.format) {
          if (dataColumn.type === EnumColumnType.DateTime) {
            return moment(value).format("DD/MM/yyyy HH:mm:ss.SSS");
          }
          return moment(value).format("DD/MM/yyyy");
        }
        return moment(value).format(dataColumn.format);
      } catch (e) {
        return "01/01/1900";
      }
    };
  }

  createNumberFormatter(dataColumn: Field): (params: ValueFormatterParams) => string {
    return (params: ValueFormatterParams) => {
      const value = params.value;
      if (value === "null" || value === null || value === "") {
        return "";
      }

      try {
        if (dataColumn.isCurrency) {
          return this.currencyPipe.transform(value, "BRL");
        }
        if (dataColumn.format != null) {
          return this.decimalPipe.transform(value, dataColumn.format);
        }
        return this.decimalPipe.transform(value);
      } catch (e) {
        return "-1";
      }
    };
  }

  // ─────────────────── estado de colunas (preferência) ───────────────────

  private resourceId(): string {
    return 'dashboard_table_configuration_' + this.idDashBoard + "_" + this.tabName + "_" + DashBoardTypeEnum.table + "_" + this.name;
  }

  private applyColumnState(api: GridApi): void {
    if (this.userConfig?.columnState != null) {
      api.applyColumnState({ state: this.userConfig.columnState, applyOrder: true });
    }
  }

  private stateSave(): void {
    if (this.gridApi == null) {
      return;
    }
    if (this.userConfig == null) {
      this.userConfig = { columnState: this.gridApi.getColumnState() };
    } else {
      this.userConfig.columnState = this.gridApi.getColumnState();
    }
    this.stateSave$.next(); // debounced — sem HTTP por pixel de resize
  }

  private saveUserConfig(): void {
    this.dvs.saveUserConfig(this.uas.user.iduser, this.resourceId(), this.userConfig)
      .catch(() => { /* erro já notificado pelo service */ });
  }

  sortChanged(): void {
    this.stateSave();
  }

  public onReoder(evento: ColumnMovedEvent): void {
    if (evento.finished) {
      this.stateSave();
    }
  }

  public onPinned(_evento: ColumnPinnedEvent): void {
    this.stateSave();
  }

  public onResize(event: ColumnResizedEvent): void {
    if (event.finished) {
      this.stateSave();
    }
  }

  // ─────────────────── ações ───────────────────

  exportCsv(): void {
    this.gridApi?.exportDataAsCsv({
      fileName: this.tabName + '.csv',
      columnSeparator: ';'
    });
  }

  selectedData(event: RowSelectedEvent): void {
    const index = this.selectedRows.findIndex(d => d.index == event.rowIndex);
    if (index > -1) {
      this.selectedRows.splice(index, 1);
    } else {
      this.selectedRows.push({ index: event.rowIndex, data: event.data });
    }
  }

  excluir(): void {
    this.deleting.emit({ tabName: this.tabName, name: this.name, type: DashBoardTypeEnum.table });
  }

  openConfiguration(): void {

    const modalRef = openGadgetModal(this.modal, this.viewContainerRef, TableCardConfigurationComponent, {
      item: this.item,
      name: this.name,
      tabName: this.tabName,
      idDashBoard: this.idDashBoard,
      filters: this.dashboard.filter != null ? this.dashboard.filter.map(d => [d.nome]) : null,
      type: DashBoardTypeEnum.table
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value == null) {
          return;
        }
        this.item = JSON.parse(JSON.stringify(value)) as TableInterface;
        this.itemChange.emit(this.item);
        this.showPagination = this.item?.options?.showPagination ?? true;
        this.canClipboard = this.item?.options?.canClipboard ?? false;
        this.canExport = this.item?.options?.canExport ?? false;
        this.refreshSeconds$.next(this.item?.refresh ?? null);
        this.reload();
      });
  }

  deletePreference(): void {
    this.dvs.removeDashBoardUserPreference(this.resourceId()).then(() => {
      this.userConfig = null;
      this.message.success("Preferências excluídas.");
    }).catch(() => { /* erro já notificado pelo service */ });
  }
}
